import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  useMediaQuery,
  Divider,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

// 移动端视频播放器容器 - 优化设计
const MobileVideoContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  paddingBottom: "56.25%", // 16:9 比例
  height: 0,
  overflow: "hidden",
  backgroundColor: "#000",
  borderRadius: "0 0 16px 16px",
  boxShadow: theme.palette.mode === "dark"
    ? "0 4px 16px rgba(0, 0, 0, 0.3)"
    : "0 4px 16px rgba(0, 0, 0, 0.1)",
  "& iframe": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    border: "none",
    borderRadius: "0 0 16px 16px",
  },
}));

// 历史列表项 - 优化设计，企业化风格
const HistoryItem = styled(Box)(({ theme, selected }) => ({
  padding: theme.spacing(1.25, 1.5),
  cursor: "pointer",
  transition: "all 0.2s ease",
  borderRadius: "10px",
  mx: -1.5,
  backgroundColor: selected
    ? theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.08)"
      : "rgba(0, 0, 0, 0.04)"
    : "transparent",
  border: selected
    ? theme.palette.mode === "dark"
      ? "0.5px solid rgba(255, 255, 255, 0.1)"
      : "0.5px solid rgba(0, 0, 0, 0.08)"
    : "0.5px solid transparent",
  boxShadow: selected
    ? theme.palette.mode === "dark"
      ? "0 2px 8px rgba(0, 0, 0, 0.15)"
      : "0 2px 8px rgba(0, 0, 0, 0.05)"
    : "none",
  "&:active": {
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.12)"
      : "rgba(0, 0, 0, 0.06)",
    transform: "scale(0.98)",
  },
}));

const MobileWorship = () => {
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const [worships, setWorships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorship, setSelectedWorship] = useState(null);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);

  useEffect(() => {
    const fetchWorships = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/worship`);
        if (Array.isArray(response.data)) {
          setWorships(response.data);
          if (response.data.length > 0) {
            setSelectedWorship(response.data[0]);
          }
        }
      } catch (err) {
        console.error("获取崇拜数据失败:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchWorships();
  }, []);

  // 从YouTube URL中提取视频ID
  const getYoutubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  // 构建YouTube移动端优化的embed URL
  const getYoutubeEmbedUrl = (videoId) => {
    if (!videoId) return "";
    // 添加移动端优化参数
    return `https://www.youtube.com/embed/${videoId}?playsinline=1&rel=0&modestbranding=1`;
  };

  const handleWorshipSelect = (worship) => {
    setIsLoadingVideo(true);
    setSelectedWorship(worship);
    // 滚动到顶部
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const textColor = prefersDarkMode ? "#ffffff" : "#000000";
  const secondaryTextColor = prefersDarkMode ? "rgba(235, 235, 245, 0.6)" : "rgba(60, 60, 67, 0.6)";
  const borderColor = prefersDarkMode ? "rgba(84, 84, 88, 0.65)" : "rgba(0, 0, 0, 0.1)";

  return (
    <Box sx={{ pb: 2 }}>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} sx={{ color: textColor }} />
        </Box>
      ) : selectedWorship ? (
        <>
          {/* 页面标题 */}
          <Box sx={{ px: 2, pt: 2.5, pb: 1.5 }}>
            <Typography
              variant="h4"
              sx={{
                fontSize: "1.5rem",
                fontWeight: 700,
                color: textColor,
                mb: 0.5,
                lineHeight: 1.2,
                letterSpacing: "-0.02em",
              }}
            >
              3CCA崇拜
            </Typography>
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: secondaryTextColor,
                letterSpacing: "0.01em",
              }}
            >
              每周持续更新
            </Typography>
          </Box>

          {/* 视频播放器 - 优化设计 */}
          {selectedWorship.videoUrl ? (
            <MobileVideoContainer>
              {isLoadingVideo && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(0, 0, 0, 0.6)",
                    zIndex: 2,
                    borderRadius: "0 0 16px 16px",
                  }}
                >
                  <CircularProgress
                    size={32}
                    sx={{
                      color: "rgba(255, 255, 255, 0.9)",
                    }}
                  />
                </Box>
              )}
              <iframe
                src={getYoutubeEmbedUrl(getYoutubeVideoId(selectedWorship.videoUrl))}
                title={selectedWorship.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                playsInline
                onLoad={() => setIsLoadingVideo(false)}
                style={{
                  width: "100%",
                  height: "100%",
                }}
              />
            </MobileVideoContainer>
          ) : (
            <Box
              sx={{
                width: "100%",
                paddingBottom: "56.25%",
                backgroundColor: "#000",
                borderRadius: "0 0 16px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              <Typography
                sx={{
                  position: "absolute",
                  color: "rgba(255, 255, 255, 0.6)",
                  fontSize: "0.85rem",
                }}
              >
                暂无视频
              </Typography>
            </Box>
          )}

          {/* 视频信息区域 - 优化布局和间距 */}
          <Box sx={{ px: 2, pt: 2 }}>
            {/* 标题 */}
            <Typography
              sx={{
                fontSize: "1.1rem",
                fontWeight: 600,
                color: textColor,
                mb: 1,
                lineHeight: 1.3,
                letterSpacing: "-0.01em",
              }}
            >
              {selectedWorship.title}
            </Typography>

            {/* 日期 */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                mb: 1.5,
              }}
            >
              <CalendarTodayIcon sx={{ fontSize: "0.85rem", color: secondaryTextColor }} />
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: secondaryTextColor,
                  letterSpacing: "0.01em",
                }}
              >
                {selectedWorship.displayDate}
              </Typography>
            </Box>

            {/* 描述 */}
            {selectedWorship.description && (
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  color: textColor,
                  lineHeight: 1.65,
                  whiteSpace: "pre-line",
                  mb: 2.5,
                  letterSpacing: "0.01em",
                }}
              >
                {selectedWorship.description}
              </Typography>
            )}

            <Divider sx={{ borderColor: borderColor, mb: 2 }} />

            {/* 历史崇拜列表 - 优化设计 */}
            {worships.length > 0 && (
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: textColor,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    mb: 1.5,
                  }}
                >
                  历史崇拜
                </Typography>
                <Box>
                  {worships.map((worship, index) => (
                    <React.Fragment key={worship.id}>
                      <HistoryItem
                        selected={selectedWorship?.id === worship.id}
                        onClick={() => handleWorshipSelect(worship)}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: selectedWorship?.id === worship.id ? 600 : 400,
                            color: textColor,
                            mb: 0.5,
                            lineHeight: 1.4,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            letterSpacing: "-0.01em",
                          }}
                        >
                          {worship.title}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <CalendarTodayIcon sx={{ fontSize: "0.65rem", color: secondaryTextColor }} />
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color: secondaryTextColor,
                              letterSpacing: "0.01em",
                            }}
                          >
                            {worship.displayDate}
                          </Typography>
                        </Box>
                      </HistoryItem>
                      {index < worships.length - 1 && (
                        <Divider
                          sx={{
                            borderColor: borderColor,
                            my: 0.75,
                          }}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </>
      ) : (
        <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
          <Typography sx={{ color: secondaryTextColor, fontSize: "0.9rem" }}>
            暂无崇拜记录
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MobileWorship;
